import React, { createContext, useContext, useMemo } from 'react';
import type { MediaBrandSettings, BrandedViewerConfig, SharedVideoData } from './types';

interface BrandedViewerContextValue {
  config: BrandedViewerConfig;
  video: SharedVideoData;
  effectivePrimary: string;
  effectiveSecondary: string;
  effectiveLogoUrl: string | null;
  effectiveLogoPosition: 'top-left' | 'top-right';
  effectiveCtaLabel: string | null;
  effectiveCtaUrl: string | null;
  showSocialShare: boolean;
  agencyName: string | null;
}

const BrandedViewerContext = createContext<BrandedViewerContextValue | null>(null);

export function useBrandedViewer() {
  const ctx = useContext(BrandedViewerContext);
  if (!ctx) throw new Error('useBrandedViewer must be used within BrandedViewerProvider');
  return ctx;
}

interface BrandedViewerProviderProps {
  config: BrandedViewerConfig;
  video: SharedVideoData;
  children: React.ReactNode;
}

export function BrandedViewerProvider({ config, video, children }: BrandedViewerProviderProps) {
  const value = useMemo<BrandedViewerContextValue>(() => ({
    config,
    video,
    effectivePrimary: video.brand_primary || '#7c3aed',
    effectiveSecondary: video.brand_secondary || '#ec4899',
    effectiveLogoUrl: video.logo_url,
    effectiveLogoPosition: (video.brand_logo_position as 'top-left' | 'top-right') || 'top-left',
    effectiveCtaLabel: video.brand_cta_label || null,
    effectiveCtaUrl: video.brand_cta_url || null,
    showSocialShare: video.brand_show_social !== false,
    agencyName: video.brand_agency_name || null,
  }), [config, video]);

  return (
    <BrandedViewerContext.Provider value={value}>
      {children}
    </BrandedViewerContext.Provider>
  );
}
